/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable react/no-danger */
import { useSpring, animated } from '@react-spring/web';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useModal } from '../../contexts/ModalsContext';
import { useWindowSize } from '../../contexts/SiteContext';
import Text from '../blocks/Text';
import DownloadButton from '../buttons/Download';
import Next from '../buttons/Next';
import Cards from '../lists/Cards';

import styles from '../../../styles/partials/slider.module.scss';

const propTypes = {
    cards: AppPropTypes.cards,
    cardsTheme: PropTypes.string,
    detailsClassName: PropTypes.string,
    textColor: PropTypes.string,
    title: PropTypes.string,
    titleClassName: PropTypes.string,
    description: PropTypes.string,
    descriptionClassName: PropTypes.string,
    LogoComponent: PropTypes.string,
    logoClassName: PropTypes.string,
    mediaKit: PropTypes.string,
    downloadIcon: PropTypes.string,
    downloadButtonClassName: PropTypes.string,
    cardClassName: PropTypes.string,
    cardsClassName: PropTypes.string,
    sliderClassName: PropTypes.string,
    withControls: PropTypes.bool,
    animation: PropTypes.shape({}),
    accentColor: PropTypes.string,
    className: PropTypes.string,
    visible: PropTypes.bool,
};

const defaultProps = {
    cards: [],
    cardsTheme: null,
    detailsClassName: null,
    textColor: null,
    title: null,
    titleClassName: null,
    description: null,
    descriptionClassName: null,
    LogoComponent: null,
    logoClassName: null,
    mediaKit: null,
    downloadIcon: null,
    downloadButtonClassName: null,
    cardClassName: null,
    cardsClassName: null,
    sliderClassName: null,
    withControls: true,
    animation: null,
    accentColor: null,
    className: null,
    visible: true,
};

const SliderPartial = ({
    cards,
    cardsTheme,
    detailsClassName,
    textColor,
    description,
    descriptionClassName,
    title,
    titleClassName,
    LogoComponent,
    logoClassName,
    mediaKit,
    downloadIcon,
    downloadButtonClassName,
    cardClassName,
    cardsClassName,
    sliderClassName,
    withControls,
    animation,
    accentColor,
    className,
    visible,
}) => {
    const { width: windowWidth } = useWindowSize();
    const sliderRef = useRef(null);
    const scrollRef = useRef(null);
    // const [scrollWidth, setScrollWidth] = useState(0);
    const [scrolled, setScrolled] = useState(false);
    const [scrollable, setScrollable] = useState(true);
    const [clicked, setClicked] = useState(false);
    const { setModal } = useModal();
    const setMediaKitModal = useCallback(() => {
        // For testing only
        // e.preventDefault();
        setModal({
            type: 'media-kit',
        });
    }, [setModal]);
    // const clickPos = useRef(0);

    const { url: downloadUrl = null } = mediaKit || {};

    const hasLogo = LogoComponent !== null;
    const hasTitle = title !== null;
    const hasDescription = description !== null;
    const hasMediaKit = mediaKit !== null;
    const hasDetails = hasLogo || hasTitle || hasDescription;

    const [{ opacity }, api] = useSpring(() => ({
        to: {
            opacity: 1,
        },
        ...animation,
    }));

    useEffect(() => {
        if (sliderRef !== null && sliderRef.current !== null) {
            const sliderWidth = 272 * cards.length + 32;
            // TODO with real card width
            const windowWidthToCheck = windowWidth > 768 ? windowWidth / 2 : windowWidth;
            setScrollable(windowWidthToCheck < sliderWidth);
        }
    }, [windowWidth, setScrollable]);

    const onClick = useCallback(() => {
        if (sliderRef !== null) {
            const minOpacity = 0.03;
            const offset =
                Math.round((1 / (sliderRef.current.scrollLeft || 1)) * 100) / 100 + minOpacity;
            if (offset && window.innerWidth > 768) {
                api.start({ to: { opacity: offset }, ...animation });
            }
        }
    }, [api, cards, sliderRef, animation]);

    const onSliderScroll = useCallback(() => {
        if (sliderRef !== null) {
            const minOpacity = 0.03;
            const offset =
                Math.round((1 / (sliderRef.current.scrollLeft || 1)) * 100) / 100 + minOpacity;
            if (offset && window.innerWidth > 768) {
                api.start({ to: { opacity: offset }, ...animation });
            }
        }
    }, [api, cards, sliderRef, animation]);

    const onScroll = useCallback(
        (position) => {
            if (position > 2) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
            scrollRef.current = position;
        },
        [scrolled, setScrolled],
    );

    const onClickNext = useCallback(() => {
        if (scrollable) {
            const position = sliderRef.current.scrollLeft + 328;
            sliderRef.current.scroll({ left: position, behavior: 'smooth' });
            onScroll(position);
            onClick();
        }
    }, [clicked, setClicked, scrollable, onClick]);

    const onClickPrevious = useCallback(() => {
        if (scrollable) {
            const position = Math.max(sliderRef.current.scrollLeft - 328, 0);
            sliderRef.current.scroll({ left: position, behavior: 'smooth' });
            onScroll(position);
            onClick();
        }
    }, [clicked, setClicked, scrollable, onClick, setScrolled]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.hasDetails]: hasDetails,
                    [styles.visible]: visible,
                    [className]: className !== null,
                },
            ])}
        >
            <animated.div
                className={classNames(styles.detailsContainer, {
                    [detailsClassName]: detailsClassName !== null,
                })}
                style={{ opacity, color: textColor !== null ? textColor : null }}
            >
                {hasDetails ? (
                    <div className={styles.detailsInner}>
                        {hasLogo ? (
                            <LogoComponent
                                className={classNames([
                                    styles.logo,
                                    {
                                        [logoClassName]: logoClassName !== null,
                                    },
                                ])}
                            />
                        ) : (
                            <>
                                {hasTitle ? (
                                    <h4
                                        className={classNames([
                                            styles.title,
                                            {
                                                [titleClassName]: titleClassName !== null,
                                            },
                                        ])}
                                    >
                                        {title}
                                    </h4>
                                ) : null}
                            </>
                        )}
                        {hasDescription ? (
                            <Text
                                className={classNames([
                                    styles.description,
                                    {
                                        [descriptionClassName]: descriptionClassName !== null,
                                    },
                                ])}
                                body={description}
                            />
                        ) : null}
                        {hasMediaKit ? (
                            <DownloadButton
                                image={downloadIcon}
                                className={classNames([
                                    styles.downloadButton,
                                    {
                                        [downloadButtonClassName]: downloadButtonClassName !== null,
                                    },
                                ])}
                                href={downloadUrl}
                                onClick={setMediaKitModal}
                                external
                                download
                            >
                                <FormattedMessage
                                    defaultMessage="Download the media kit"
                                    label="Button label"
                                />
                            </DownloadButton>
                        ) : null}
                    </div>
                ) : null}
            </animated.div>
            <div
                className={classNames([
                    styles.slider,
                    { [sliderClassName]: sliderClassName !== null },
                ])}
            >
                {scrolled && withControls ? (
                    <Next
                        className={classNames([styles.button, styles.previous])}
                        onClick={onClickPrevious}
                        direction="left"
                        style={{
                            backgroundColor:
                                accentColor === 'black'
                                    ? `rgba(0,0,0, 0.1)`
                                    : `rgba(255,255,255, 0.1)`,
                        }}
                    />
                ) : null}
                <Cards
                    className={classNames([
                        styles.cardsList,
                        {
                            [styles.scrollable]: scrollable,
                            [cardsClassName]: cardsClassName !== null,
                        },
                    ])}
                    cardClassName={cardClassName}
                    items={cards}
                    theme={cardsTheme}
                    ref={sliderRef}
                    onScroll={onSliderScroll}
                    visible={visible}
                />
                {withControls ? (
                    <Next
                        className={classNames([styles.button, styles.next])}
                        onClick={onClickNext}
                        style={{
                            backgroundColor:
                                accentColor === 'black'
                                    ? `rgba(0,0,0, 0.1)`
                                    : `rgba(255,255,255, 0.1)`,
                        }}
                    />
                ) : null}
            </div>
        </div>
    );
};

SliderPartial.propTypes = propTypes;
SliderPartial.defaultProps = defaultProps;

export default SliderPartial;
